import { useOrganization } from "../../context/OrganizationContext";
import { FaChevronDown } from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import OptimizedImage from "../SEO/OptimizedImage";

const OrganizationHeader = () => {
  const {
    organizations,
    currentOrganization,
    loading,
    error,
    switchOrganization,
  } = useOrganization();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <header className="bg-brand-charcoal text-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3 animate-pulse">
            <div className="h-10 w-10 bg-brand-mist/30 rounded-lg" />
            <div className="h-5 w-40 bg-brand-mist/30 rounded" />
          </div>
        </div>
      </header>
    );
  }

  if (error) {
    return (
      <header className="bg-brand-charcoal text-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-red-300">
            Error loading organization: {error}
          </div>
        </div>
      </header>
    );
  }

  const organizationName = currentOrganization?.name || "No Organization";
  const abbreviation = currentOrganization?.nameAbbreviation;

  const nameLabel = (
    <span className="min-w-0 break-words">
      {organizationName}
      {abbreviation ? (
        <span className="text-base font-normal text-brand-mist ml-2">
          ({abbreviation})
        </span>
      ) : null}
    </span>
  );

  return (
    <header className="bg-brand-charcoal text-brand-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center gap-3 min-w-0">
          {currentOrganization?.logoUrl ? (
            <OptimizedImage
              src={currentOrganization.logoUrl}
              alt={`${currentOrganization.name} organization logo`}
              width={32}
              height={32}
              className="h-12 w-12 shrink-0 rounded-lg"
            />
          ) : (
            <div className="h-12 w-12 shrink-0 rounded-lg bg-brand-line flex items-center justify-center">
              <span className="text-2xl font-display font-bold text-brand-cream">
                {currentOrganization?.name?.charAt(0)}
              </span>
            </div>
          )}
          <div className="relative min-w-0 flex-1" ref={dropdownRef}>
            {organizations.length > 1 ? (
              <div>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 min-h-11 max-w-full text-left font-display font-bold text-lg sm:text-xl text-brand-cream hover:text-brand-gold"
                  aria-expanded={isDropdownOpen}
                  aria-haspopup="listbox"
                >
                  {nameLabel}
                  <FaChevronDown className="h-4 w-4 shrink-0" />
                </button>
                {isDropdownOpen && (
                  <div className="absolute left-0 mt-2 w-56 max-w-[calc(100vw-2rem)] card-board z-40 overflow-hidden">
                    <div className="py-1" role="listbox">
                      {organizations.map((org) => (
                        <button
                          type="button"
                          key={org.id}
                          onClick={() => {
                            switchOrganization(org.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`${
                            currentOrganization?.id === org.id
                              ? "bg-brand-charcoal text-brand-cream"
                              : "text-brand-charcoal hover:bg-brand-cream"
                          } block w-full min-h-11 px-4 py-2 text-left text-sm break-words`}
                        >
                          {org.name}
                          {org.nameAbbreviation ? (
                            <span className="opacity-70 ml-1">
                              ({org.nameAbbreviation})
                            </span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <h1 className="min-h-11 flex items-center font-display font-bold text-lg sm:text-xl text-brand-cream break-words">
                {nameLabel}
              </h1>
            )}
            {currentOrganization?.description ? (
              <p className="text-sm text-brand-mist break-words">
                {currentOrganization.description}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
};

export default OrganizationHeader;
